/**
 * Clean Enterprise — calm, professional light design language for App mode.
 * Blue accent, airy whitespace, flat hairline borders, mono for numbers.
 * Tokens are exported so primitives/screens can share the exact values.
 */
import { createTheme, Theme } from "@mui/material/styles";
import type { ThemeConfig } from "./types";
import { MONO_FONT } from "@/shared/typography";
import { buildOperatorMuiComponents } from "@/shared/operator";

export const cleanEnterpriseTokens = {
  bg: "#F7F8FA",
  surface: "#FFFFFF",
  surface2: "#FAFBFC",
  ink: "#1F2937",
  ink2: "#6B7280",
  ink3: "#9CA3AF",
  accent: "#2563EB",
  accentLight: "#DBEAFE",
  accentDark: "#1E40AF",
  success: "#16A34A",
  successLight: "#DCFCE7",
  warning: "#D97706",
  warningLight: "#FEF3C7",
  error: "#DC2626",
  errorLight: "#FEE2E2",
  info: "#2563EB",
  infoLight: "#DBEAFE",
  border: "#E5E9EF",
  sans: '"Instrument Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: MONO_FONT,
} as const;

export const createCleanEnterpriseTheme = (): Theme => {
  const t = cleanEnterpriseTokens;
  return createTheme({
    palette: {
      mode: "light",
      primary: { main: t.accent, light: t.accentLight, dark: t.accentDark },
      background: { default: t.bg, paper: t.surface },
      text: { primary: t.ink, secondary: t.ink2, disabled: t.ink3 },
      success: { main: t.success },
      warning: { main: t.warning },
      error: { main: t.error },
      info: { main: t.info },
      divider: t.border,
    },
    typography: {
      fontFamily: t.sans,
      fontSize: 13,
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 500 },
      overline: {
        fontFamily: t.mono,
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
    },
    shape: { borderRadius: 8 },
    components: buildOperatorMuiComponents(t),
  });
};

export const cleanEnterpriseThemeConfig: ThemeConfig = {
  id: "clean-enterprise",
  name: "Clean Enterprise",
  description: "Calm professional light design language — blue accent, airy, flat hairline borders",
  createTheme: createCleanEnterpriseTheme,
};
